<template>
  <div class="tender-views">
    <!-- Tabs row: scrollable view tabs + right-hand actions. Phones switch views from the page
         title instead (openViewsSheet), unless there's no view yet to switch to. -->
    <div
      v-if="!isPhone || !allViews.length"
      class="tender-views__bar"
      :class="{ 'tender-views__bar--empty': !allViews.length }"
    >
      <!-- Overflowing tabs scroll horizontally; edge fades + hover arrows signal there's more,
           and the active tab is auto-scrolled into view. Full list lives in "Voir toutes les vues". -->
      <div class="tender-views__tabs" :class="{ 'is-ovl-left': canScrollLeft, 'is-ovl-right': canScrollRight }">
        <button
          v-show="canScrollLeft"
          type="button"
          class="tender-views__scroll-btn tender-views__scroll-btn--left"
          :aria-label="$t('page.tenders.views.scroll-tabs-prev')"
          @click="scrollTabs(-1)"
        >
          <BaseIcon name="chevron-left" />
        </button>
        <div class="tender-views__tabscroll" role="tablist" ref="tabscroll" @scroll="updateScrollState">
          <button
            v-for="view in allViews"
            :key="view.id"
            class="tender-views__tab"
            :class="{
              'is-active': view.id === activeViewId,
              'is-dragover': dragOverId === view.id,
              'is-dragging': dragViewId === view.id,
            }"
            role="tab"
            :aria-selected="view.id === activeViewId"
            draggable="true"
            @click="onSelect(view)"
            @dragstart="onDragStart(view.id, $event)"
            @dragend="onDragEnd"
            @dragover.prevent="dragOverId = view.id"
            @dragleave="dragOverId = null"
            @drop.prevent="onDrop(view.id)"
          >
            <span class="tender-views__grip"><BaseIcon name="grip-vertical" /></span>
            <BaseIcon
              :name="viewIcon(view)"
              class="tender-views__tab-ico"
              :class="{ 'is-preset': isDefaultView(view) }"
            />
            <span class="tender-views__tab-label">{{ viewLabel(view) }}</span>
          </button>
        </div>
        <button
          v-show="canScrollRight"
          type="button"
          class="tender-views__scroll-btn tender-views__scroll-btn--right"
          :aria-label="$t('page.tenders.views.scroll-tabs-next')"
          @click="scrollTabs(1)"
        >
          <BaseIcon name="chevron-right" />
        </button>
      </div>

      <div class="tender-views__actions">
        <BaseButton
          v-if="allViews.length"
          ref="allViewsBtn"
          isSmall
          isOutlined
          isSquare
          hasIconRight
          @click.native.stop="toggleAllViews"
        >
          <span>{{ $t('page.tenders.views.all-views') }}</span>
          <BaseIcon name="chevron-down" />
        </BaseButton>
        <BaseButton isSmall isSquare hasIconLeft @click.native="onCreate">
          <BaseIcon name="plus" />
          <span>{{ $t('page.tenders.views.add') }}</span>
        </BaseButton>
        <!-- View actions (⋯) menu. Shows for any view: edit items are gated by canEdit inside
             menuItems, but per-user actions (aviseur, duplicate) are always available. -->
        <div v-if="menuItems.length" class="tender-views__more">
          <button
            ref="moreBtn"
            type="button"
            class="tender-views__more-btn"
            :class="{ 'is-open': moreOpen }"
            :aria-label="$t('page.tenders.views.more-actions')"
            @click.stop="toggleMore"
          >
            <BaseIcon name="more-vertical" />
          </button>
          <div v-if="moreOpen" v-closable="closeMore" class="tender-views__moremenu" @click.stop>
            <template v-for="(item, i) in menuItems">
              <div v-if="item.divider" :key="`div-${i}`" class="tender-views__moremenu-div"></div>
              <button
                v-else
                :key="item.key"
                type="button"
                class="tender-views__moremenu-item"
                :class="{ 'is-danger': item.danger, 'is-disabled': item.disabled }"
                :aria-disabled="item.disabled ? 'true' : null"
                v-tooltip="{ content: item.tooltip, placement: 'left' }"
                @click="onMoreItem(item)"
              >
                <span class="tender-views__moremenu-ic"><BaseIcon :name="item.icon" /></span>
                <span class="tender-views__moremenu-label">{{ item.title }}</span>
                <BaseIcon v-if="item.checked" name="check" class="tender-views__moremenu-chk" />
              </button>
            </template>
          </div>
        </div>

        <!-- "Voir toutes les vues" dropdown (select + drag-reorder) -->
        <div v-if="allViewsOpen" v-closable="closeAllViews" class="tender-views__allviews" @click.stop>
          <div class="tender-views__allviews-title">{{ $t('page.tenders.views.reorder-hint') }}</div>
          <div
            v-for="view in allViews"
            :key="view.id"
            class="tender-views__allviews-row"
            :class="{ 'is-dragover': dragOverId === view.id, 'is-dragging': dragViewId === view.id }"
            draggable="true"
            @click="onSelectFromMenu(view)"
            @dragstart="onDragStart(view.id, $event)"
            @dragend="onDragEnd"
            @dragover.prevent="dragOverId = view.id"
            @dragleave="dragOverId = null"
            @drop.prevent="onDrop(view.id)"
          >
            <span class="tender-views__allviews-grip"><BaseIcon name="grip-vertical" /></span>
            <BaseIcon
              :name="viewIcon(view)"
              class="tender-views__allviews-ico"
              :class="{ 'is-preset': isDefaultView(view) }"
            />
            <span class="tender-views__allviews-name">{{ viewLabel(view) }}</span>
            <BaseIcon v-if="view.id === activeViewId" name="check" class="tender-views__allviews-check" />
          </div>
        </div>
      </div>
    </div>

    <!-- Filters go between the tabs and the view meta -->
    <slot name="filters" />

    <!-- Active view meta: scope + Aviseur badges (left), dirty save-state (right). Phones show the
         badges in the views sheet, and this row only for unsaved changes. -->
    <div class="tender-views__meta" v-if="activeView && (!isPhone || isActiveViewDirty)">
      <div v-if="!isPhone" class="tender-views__badges">
        <BaseTag v-for="badge in badges" :key="badge.key" :tone="badge.tone" :icon="badge.icon" preset="soft">
          {{ badge.text }}
        </BaseTag>
      </div>

      <!-- Dirty save-state: working filters differ from the saved view -->
      <div v-if="isActiveViewDirty" class="tender-views__save">
        <span class="tender-views__dirty-note">{{ $t('page.tenders.views.modified') }}</span>
        <BaseButton isSmall isOutlined isSquare @click.native="onReset">
          {{ $t('page.tenders.views.reset') }}
        </BaseButton>
        <!-- Phones: one button, the two ways to save in a sheet. -->
        <BaseButton v-if="isPhone" isSmall isSquare @click.native="saveSheetOpen = true">
          {{ $t('page.tenders.views.save') }}
        </BaseButton>
        <template v-else>
          <!-- Save-as-new is secondary while the view is editable (Save changes is the primary), and
               becomes the primary (filled) when Save changes is disabled on an uneditable view. -->
          <BaseButton isSmall isSquare :isOutlined="canEditActiveView" @click.native="openSaveAsNew">
            {{ $t('page.tenders.views.save-as-new') }}
          </BaseButton>
          <!-- Primary action, kept last (rightmost). On views the user can't edit it stays visible
               but disabled, with a tooltip explaining why (rather than being hidden). -->
          <BaseButton
            class="tender-views__save-changes"
            :class="{ 'is-locked': !canEditActiveView }"
            isSmall
            isSquare
            :disabled="!canEditActiveView"
            :tooltipText="updateDisabledReason"
            @click.native="onUpdate"
          >
            {{ $t('page.tenders.views.update') }}
          </BaseButton>
        </template>
      </div>
    </div>

    <!-- Phones: every view, a new one, and what can be done with the one shown. -->
    <BaseBottomSheet v-if="isPhone" v-model="viewsSheetOpen" :title="$t('page.tenders.views.sheet-title')">
      <ul class="base-bottom-sheet__menu">
        <li v-for="view in allViews" :key="view.id">
          <button
            type="button"
            :class="{ 'is-active': view.id === activeViewId }"
            :aria-current="view.id === activeViewId ? 'true' : null"
            @click="fromViewsSheet(() => onSelect(view))"
          >
            <BaseIcon
              :name="viewIcon(view)"
              class="tender-views__sheet-ico"
              :class="{ 'is-preset': isDefaultView(view) }"
            />
            <span class="tender-views__sheet-name">{{ viewLabel(view) }}</span>
            <BaseIcon v-if="view.id === activeViewId" name="check" class="base-bottom-sheet__menu-check" />
          </button>
        </li>
        <li>
          <button type="button" @click="fromViewsSheet(onCreate)">
            <BaseIcon name="plus" />
            {{ $t('page.tenders.views.add') }}
          </button>
        </li>
      </ul>
      <template v-if="activeView">
        <div class="tender-views__sheet-current">
          <p class="tender-views__sheet-heading">{{ viewLabel(activeView) }}</p>
          <div class="tender-views__badges">
            <BaseTag v-for="badge in badges" :key="badge.key" :tone="badge.tone" :icon="badge.icon" preset="soft">
              {{ badge.text }}
            </BaseTag>
          </div>
        </div>
        <ul class="base-bottom-sheet__menu">
          <li v-for="item in sheetMenuItems" :key="item.key">
            <button
              type="button"
              :class="{ 'is-danger': item.danger }"
              :disabled="item.disabled"
              @click="fromViewsSheet(item.onClick)"
            >
              <BaseIcon :name="item.icon" />
              {{ item.title }}
            </button>
            <p v-if="item.disabled && item.tooltip" class="base-bottom-sheet__menu-note">{{ item.tooltip }}</p>
          </li>
        </ul>
      </template>
    </BaseBottomSheet>

    <BaseBottomSheet v-if="isPhone" v-model="saveSheetOpen" :title="$t('page.tenders.views.modified')">
      <ul class="base-bottom-sheet__menu">
        <li>
          <button type="button" :disabled="!canEditActiveView" @click="fromSaveSheet(onUpdate)">
            <BaseIcon name="save" />
            {{ $t('page.tenders.views.update') }}
          </button>
          <p v-if="!canEditActiveView" class="base-bottom-sheet__menu-note">{{ updateDisabledReason }}</p>
        </li>
        <li>
          <button type="button" @click="fromSaveSheet(openSaveAsNew)">
            <BaseIcon name="plus" />
            {{ $t('page.tenders.views.save-as-new') }}
          </button>
        </li>
      </ul>
    </BaseBottomSheet>

    <!-- Rename / Create / Save-as-new dialog -->
    <BaseDialog
      centered
      ref="formDialog"
      contentClass="tender-view-dialog"
      :show-actions="true"
      @closeModal="closeForm"
      :onClose="closeForm"
    >
      <template slot="title">{{ formTitle }}</template>
      <template slot="content">
        <div class="tender-views__field">
          <label class="tender-views__field-label">{{ $t('page.tenders.views.name-label') }}</label>
          <FormInput
            ref="nameInput"
            name="view-name"
            :maxlength="viewNameMaxLength"
            :value="form.name"
            :placeholder="$t('page.tenders.views.name-placeholder')"
            @input="form.name = $event"
            @keyup.native.enter="submitForm"
          />
        </div>

        <template v-if="form.mode !== 'rename'">
          <div class="tender-views__field">
            <label class="tender-views__field-label">{{ $t('page.tenders.views.visibility') }}</label>
            <div class="tender-views__radiorow">
              <button
                type="button"
                class="tender-views__radiocard"
                :class="{ 'is-on': form.scope === PRIVATE }"
                @click="form.scope = PRIVATE"
              >
                <span class="tender-views__radiocard-title">
                  <BaseIcon name="user" />{{ $t('page.tenders.views.scope-private') }}
                </span>
                <span class="tender-views__radiocard-desc">{{ $t('page.tenders.views.scope-private-desc') }}</span>
              </button>
              <button
                type="button"
                class="tender-views__radiocard"
                :class="{ 'is-on': form.scope === SHARED }"
                @click="form.scope = SHARED"
              >
                <span class="tender-views__radiocard-title">
                  <BaseIcon name="users" />{{ $t('page.tenders.views.scope-shared') }}
                </span>
                <span class="tender-views__radiocard-desc">{{ $t('page.tenders.views.scope-shared-desc') }}</span>
              </button>
            </div>
          </div>

          <div class="tender-views__toggle-row">
            <div class="tender-views__toggle-text">
              <div class="tender-views__toggle-title">{{ $t('dialog.filters.all-keywords') }}</div>
              <div class="tender-views__toggle-desc">{{ $t('dialog.filters.all-keywords-desc') }}</div>
            </div>
            <BaseToggle :value="form.allKeywords" is-small @change="form.allKeywords = $event" />
          </div>

          <div class="tender-views__toggle-row">
            <div class="tender-views__toggle-text">
              <div class="tender-views__toggle-title">{{ $t('page.tenders.views.aviseur') }}</div>
              <div class="tender-views__toggle-desc">{{ $t('page.tenders.views.aviseur-desc') }}</div>
            </div>
            <BaseToggle :value="form.aviseur" is-small @change="form.aviseur = $event" />
          </div>

          <div v-if="form.mode === 'save-as-new'" class="tender-views__note">
            {{ $t('page.tenders.views.filters-note') }}
          </div>
        </template>
      </template>
      <template slot="actions">
        <div class="tender-views__dialog-actions">
          <BaseButton isOutlined isSquare @click.native="closeForm">{{ $t('page.tenders.views.cancel') }}</BaseButton>
          <BaseButton :disabled="!form.name.trim()" @click.native="submitForm">{{ formSubmitLabel }}</BaseButton>
        </div>
      </template>
    </BaseDialog>

    <!-- Delete confirmation -->
    <BaseDialog
      centered
      ref="deleteDialog"
      contentClass="tender-view-confirm"
      :show-actions="true"
      @closeModal="closeDelete"
      :onClose="closeDelete"
    >
      <template slot="title">{{ $t('page.tenders.views.delete-title') }}</template>
      <template slot="content">
        <p class="tender-views__confirm-text">
          <i18n path="page.tenders.views.delete-confirm" tag="span">
            <template v-slot:name
              ><strong>{{ confirmDelete.name }}</strong></template
            >
          </i18n>
          <br />
          {{
            confirmDelete.shared
              ? $t('page.tenders.views.delete-shared-warning')
              : $t('page.tenders.views.delete-irreversible')
          }}
        </p>
      </template>
      <template slot="actions">
        <div class="tender-views__dialog-actions">
          <BaseButton isOutlined isSquare @click.native="closeDelete">{{ $t('page.tenders.views.cancel') }}</BaseButton>
          <BaseButton isDanger @click.native="confirmDeleteView">{{
            $t('page.tenders.views.delete-action')
          }}</BaseButton>
        </div>
      </template>
    </BaseDialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import BaseBottomSheet from '@/components/base/BaseBottomSheet.vue';
import BaseButton from '@/components/base/BaseButton';
import BaseDialog from '@/components/base/BaseDialog';
import BaseIcon from '@/components/base/BaseIcon';
import BaseTag from '@/components/base/BaseTag';
import BaseToggle from '@/components/base/BaseToggle';
import FormInput from '@/components/form/FormInput';
import {
  VIEW_NAME_MAX_LENGTH,
  VIEW_SCOPE,
  isPresetView,
  viewIconName,
  viewLabel as getViewLabel,
} from '@/models/tender-views';
import { viewport } from '@/util/viewport';

export default {
  name: 'TenderViews',
  components: {
    BaseBottomSheet,
    BaseButton,
    BaseDialog,
    BaseIcon,
    BaseTag,
    BaseToggle,
    FormInput,
  },
  data() {
    return {
      allViewsOpen: false,
      moreOpen: false,
      // Phones' sheets: the views, and the two ways to save changes.
      viewsSheetOpen: false,
      saveSheetOpen: false,
      dragViewId: null,
      dragOverId: null,
      // Tab-overflow scroll affordances (edge fades + hover arrows).
      canScrollLeft: false,
      canScrollRight: false,
      // Rename / create / save-as-new form dialog state.
      form: {
        mode: null, // 'rename' | 'create' | 'save-as-new'
        name: '',
        scope: VIEW_SCOPE.PRIVATE,
        allKeywords: false,
        aviseur: false,
      },
      // Delete confirmation dialog state.
      confirmDelete: {
        name: '',
        shared: false,
      },
    };
  },
  computed: {
    ...mapGetters('tenderViews', ['allViews', 'activeView', 'activeViewId', 'isActiveViewDirty', 'canEditActiveView']),
    ...mapGetters('auth', ['currentUser']),
    ...mapGetters('searchFilters', { workingSearchFilters: 'searchFilters' }),
    ...mapGetters('tenant', ['users']),
    viewNameMaxLength() {
      return VIEW_NAME_MAX_LENGTH;
    },
    isPhone() {
      return viewport.size === 'phone';
    },
    // The active view's scope, then whether its Aviseur is on.
    badges() {
      const view = this.activeView;
      if (!view) return [];
      const t = (key) => this.$t(`page.tenders.views.${key}`);
      // Private: personal to the owner.
      let scope = { tone: 'neutral', icon: 'user', text: t('scope-private') };
      if (view.preset) {
        // Preset: locked tenant default. Warning tone matches the orange lock on unknown presets.
        scope = { tone: 'warning', icon: this.activePresetBadgeIcon, text: t('preset') };
      } else if (this.isSharedWithMe) {
        // Shared view owned by a teammate (shared TO you) — name the owner so it's clear it
        // isn't yours (which is why the edit actions are gone).
        scope = { tone: 'info', icon: 'users', text: this.sharedByLabel };
      } else if (this.isShared) {
        // Your own shared view: shared with the whole team (results still resolve per user).
        scope = { tone: 'success', icon: 'users', text: t('scope-shared') };
      }
      const badges = [{ key: 'scope', ...scope }];
      if (view.aviseurEnabled) badges.push({ key: 'aviseur', tone: 'info', icon: 'bell', text: t('aviseur-on') });
      return badges;
    },
    SHARED() {
      return VIEW_SCOPE.SHARED;
    },
    PRIVATE() {
      return VIEW_SCOPE.PRIVATE;
    },
    isShared() {
      return !!this.activeView && this.activeView.scope === VIEW_SCOPE.SHARED;
    },
    // A shared view owned by someone else (shared TO the current user, not created by them).
    isSharedWithMe() {
      const v = this.activeView;
      return !!(
        v &&
        !v.preset &&
        v.scope === VIEW_SCOPE.SHARED &&
        v.ownerUserId &&
        this.currentUser &&
        v.ownerUserId !== this.currentUser.id
      );
    },
    // "Partagée par <owner>" when we can resolve the owner, else a generic "shared with you".
    sharedByLabel() {
      if (!this.activeView) return '';
      const owner = (this.users || []).find((u) => u.id === this.activeView.ownerUserId);
      const name = owner ? `${owner.firstName || ''} ${owner.lastName || ''}`.trim() : '';
      return name ? this.$t('page.tenders.views.shared-by', { name }) : this.$t('page.tenders.views.shared-with-you');
    },
    // Why the "Save changes" button is disabled — tailored to the reason (a locked preset vs a view
    // a teammate shared with you). Empty when the view is editable (no tooltip).
    updateDisabledReason() {
      if (this.canEditActiveView) return '';
      const key = this.activeView && this.activeView.preset ? 'update-disabled-preset' : 'update-disabled-shared';
      return this.$t(`page.tenders.views.${key}`);
    },
    activePresetBadgeIcon() {
      return this.activeView ? viewIconName(this.activeView) : 'lock';
    },
    formTitle() {
      const t = (k) => this.$t(`page.tenders.views.${k}`);
      if (this.form.mode === 'rename') return t('rename-title');
      if (this.form.mode === 'save-as-new') return t('save-as-new-title');
      return t('create-title');
    },
    formSubmitLabel() {
      const t = (k) => this.$t(`page.tenders.views.${k}`);
      // Create and save-as-new both create a new view → same verb ("Créer"). Rename is distinct.
      if (this.form.mode === 'rename') return t('rename-action');
      return t('create-action');
    },
    // Views the user owns (editable + not a tenant preset). `canEdit` is "owner or admin"
    // per the API, which is the closest signal we have to ownership.
    ownedViewCount() {
      return this.allViews.filter((v) => v.canEdit && !v.preset).length;
    },
    // The backend rejects deleting your only owned (non-preset) view — mirror that in the UI.
    canDeleteActiveView() {
      const v = this.activeView;
      return !!v && !(!v.preset && this.ownedViewCount <= 1);
    },
    menuItems() {
      if (!this.activeView) return [];
      const t = (k) => this.$t(`page.tenders.views.${k}`);
      const canEdit = this.canEditActiveView;
      const items = [];
      // Plain actions first, then the toggles (aviseur is available on any view).
      if (canEdit) items.push({ key: 'rename', title: t('rename'), icon: 'edit-2', onClick: this.openRename });
      items.push({ key: 'duplicate', title: t('duplicate'), icon: 'copy', onClick: this.onDuplicate });
      if (canEdit) {
        items.push({
          key: 'scope',
          title: this.isShared ? t('make-private') : t('share'),
          icon: 'share-2',
          checked: this.isShared,
          onClick: this.onToggleScope,
        });
      }
      items.push({
        key: 'aviseur',
        title: this.activeView.aviseurEnabled ? t('disable-aviseur') : t('enable-aviseur'),
        icon: 'bell',
        checked: this.activeView.aviseurEnabled,
        onClick: this.onToggleAviseur,
      });
      if (canEdit) {
        items.push({ divider: true });
        items.push({
          key: 'delete',
          title: t('delete'),
          icon: 'trash-2',
          danger: true,
          // Disabled (with an explanatory tooltip) when it's the user's only owned view —
          // the backend would 400 anyway (see the fallback in confirmDeleteView).
          disabled: !this.canDeleteActiveView,
          tooltip: this.canDeleteActiveView ? null : t('delete-last-warning'),
          onClick: this.onDelete,
        });
      }
      return items;
    },
    sheetMenuItems() {
      return this.menuItems.filter((item) => !item.divider);
    },
  },
  methods: {
    ...mapActions('tenderViews', [
      'updateActiveView',
      'createView',
      'saveAsNewView',
      'renameView',
      'toggleScope',
      'toggleAviseur',
      'duplicateView',
      'removeView',
      'resetActiveView',
      'reorder',
    ]),
    // ---- tab overflow scrolling ----
    updateScrollState() {
      const el = this.$refs.tabscroll;
      if (!el) return;
      this.canScrollLeft = el.scrollLeft > 1;
      this.canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    },
    scrollTabs(dir) {
      const el = this.$refs.tabscroll;
      if (!el) return;
      el.scrollBy({ left: dir * Math.max(120, Math.round(el.clientWidth * 0.6)), behavior: 'smooth' });
    },
    // Keep the active tab visible (e.g. after picking it from the "all views" dropdown)
    // by scrolling the container only — never the page.
    scrollActiveIntoView() {
      const el = this.$refs.tabscroll;
      if (!el) return;
      const active = el.querySelector('.tender-views__tab.is-active');
      if (!active) return;
      const c = el.getBoundingClientRect();
      const a = active.getBoundingClientRect();
      if (a.left < c.left) el.scrollLeft -= c.left - a.left + 8;
      else if (a.right > c.right) el.scrollLeft += a.right - c.right + 8;
      this.updateScrollState();
    },
    // Seeded default views carry an i18n nameKey; translate it, else show the typed name.
    viewLabel(view) {
      return getViewLabel(view);
    },
    // Icon per view kind: saved → bookmark, shared → shared,
    // other presets → lock, shared user views → group, private → person.
    viewIcon(view) {
      return viewIconName(view);
    },
    isDefaultView(view) {
      return isPresetView(view);
    },
    onSelect(view) {
      if (view.id === this.activeViewId) return;
      this.$emit('select', view);
    },
    onSelectFromMenu(view) {
      this.closeAllViews();
      this.onSelect(view);
    },
    toggleAllViews() {
      this.allViewsOpen = !this.allViewsOpen;
      this.moreOpen = false;
      if (this.allViewsOpen) this.$emit('menu-opened');
    },
    // Ignore the outside-mousedown when it originates from this dropdown's own
    // activator — otherwise v-closable (mousedown) closes it and the activator's
    // click immediately re-toggles it open.
    closeAllViews(event) {
      const btn = this.$refs.allViewsBtn;
      if (event && btn && btn.$el.contains(event.target)) return;
      this.allViewsOpen = false;
    },
    toggleMore() {
      this.moreOpen = !this.moreOpen;
      this.allViewsOpen = false;
      if (this.moreOpen) this.$emit('menu-opened');
    },
    closeMore(event) {
      const btn = this.$refs.moreBtn;
      if (event && btn && btn.contains(event.target)) return;
      this.moreOpen = false;
    },
    onMoreItem(item) {
      if (item.disabled) return;
      this.closeMore();
      item.onClick();
    },
    // ---- phones ----
    openViewsSheet() {
      this.viewsSheetOpen = true;
      this.$emit('menu-opened');
    },
    // A choice in a sheet closes it first: what follows may open a dialog of its own.
    fromViewsSheet(action) {
      this.viewsSheetOpen = false;
      action();
    },
    fromSaveSheet(action) {
      this.saveSheetOpen = false;
      action();
    },
    // ---- drag-reorder ----
    onDragStart(viewId, event) {
      this.dragViewId = viewId;
      if (event.dataTransfer) event.dataTransfer.setData('text/plain', viewId);
    },
    onDragEnd() {
      this.dragViewId = null;
      this.dragOverId = null;
    },
    onDrop(targetId) {
      const fromId = this.dragViewId;
      this.dragOverId = null;
      this.dragViewId = null;
      if (!fromId || fromId === targetId) return;
      const ids = this.allViews.map((v) => v.id);
      const from = ids.indexOf(fromId);
      const to = ids.indexOf(targetId);
      if (from < 0 || to < 0) return;
      // The dragged view takes the target's slot; the target shifts away from the drop
      // direction (forward drop lands after the target, backward lands before) so either end
      // stays reachable.
      ids.splice(to, 0, ids.splice(from, 1)[0]);
      this.reorder(ids);
    },
    // ---- view actions ----
    onCreate() {
      this.openForm('create');
    },
    onUpdate() {
      // The button is disabled on uneditable views but stays clickable-through (pointer-events:none
      // lets its tooltip show), so guard here too — clicking it must not save/toast.
      if (!this.canEditActiveView) return;
      this.updateActiveView()
        .then(() => this.toast('success', 'update-toast'))
        .catch(() => this.toast('error', 'action-error'));
    },
    onReset() {
      this.resetActiveView();
      this.$emit('refresh');
    },
    onToggleScope() {
      // Capture the direction before dispatching — the store flips view.scope in place.
      const sharing = this.activeView.scope !== VIEW_SCOPE.SHARED;
      this.toggleScope(this.activeView.id)
        .then(() => this.toast('success', sharing ? 'shared-toast' : 'private-toast'))
        .catch(() => this.toast('error', 'action-error'));
    },
    onToggleAviseur() {
      const enabling = !this.activeView.aviseurEnabled;
      this.toggleAviseur(this.activeView.id)
        .then(() => this.toast('success', enabling ? 'aviseur-on-toast' : 'aviseur-off-toast'))
        .catch(() => this.toast('error', 'action-error'));
    },
    // Confirmation feedback for view actions whose only other cue is a quiet badge change.
    toast(kind, key) {
      if (this.$toast) this.$toast[kind](this.$t(`page.tenders.views.${key}`));
    },
    onDuplicate() {
      this.duplicateView(this.activeView.id)
        .then(() => {
          this.$emit('refresh');
          this.toast('success', 'duplicate-toast');
        })
        .catch(this.onCreateError);
    },
    // A rejected create is a view-limit 400 (the dialog already prevents the other validation errors).
    onCreateError(err) {
      const limitReached = err && err.response && err.response.status === 400;
      this.toast('error', limitReached ? 'limit-error' : 'action-error');
    },
    onDelete() {
      // Deleting the last view is allowed — it drops to the empty state (bar + "+ Nouvelle vue").
      this.confirmDelete = { name: this.viewLabel(this.activeView), shared: this.isShared };
      this.$refs.deleteDialog.openModal();
    },
    closeDelete() {
      if (this.$refs.deleteDialog) this.$refs.deleteDialog.isModalOpen = false;
    },
    confirmDeleteView() {
      this.removeView(this.activeView.id)
        .then(() => {
          // If a view remains, refresh it; if that was the last one, we drop to the empty state.
          if (this.allViews.length) this.$emit('refresh');
          this.toast('success', 'delete-toast');
        })
        .catch((err) => {
          // Fallback if the backend rejects (e.g. deleting your only owned view — 400).
          const msg =
            (err && err.response && err.response.data && err.response.data.message) ||
            this.$t('page.tenders.views.delete-last-warning');
          if (this.$toast) this.$toast.error(msg);
        });
      this.closeDelete();
    },
    openRename() {
      this.openForm('rename', { name: this.activeView.name });
    },
    // Called from the dirty save-bar: persist the working filters as a brand-new view.
    openSaveAsNew() {
      this.openForm('save-as-new');
    },
    openForm(mode, { name } = {}) {
      this.form = {
        // Start blank for create/save-as-new so the placeholder shows and a meaningful name is
        // required (submit stays disabled until typed). Rename passes the current name in.
        mode,
        name: name || '',
        scope: VIEW_SCOPE.PRIVATE,
        // Only save-as-new carries the working filters; a new view starts from the defaults.
        allKeywords: mode === 'save-as-new' && !!this.workingSearchFilters.allKeywords,
        aviseur: false,
      };
      this.$refs.formDialog.openModal();
      this.focusNameInput();
    },
    // Focus the name field on open. The dialog grabs focus during its slide-in transition, so
    // focus at a few points and let the last (post-transition) call win.
    focusNameInput() {
      const focus = () => {
        const wrapper = this.$refs.nameInput;
        const input = wrapper && wrapper.$el && wrapper.$el.querySelector('input');
        if (input) {
          input.focus();
          input.select();
        }
      };
      [0, 180, 350].forEach((d) => setTimeout(focus, d));
    },
    closeForm() {
      if (this.$refs.formDialog) this.$refs.formDialog.isModalOpen = false;
    },
    submitForm() {
      // maxlength caps typing/paste; clamp here too so a programmatically set value (e.g. renaming a
      // legacy view whose name predates this limit) can't be saved over the DB cap.
      const name = this.form.name.trim().slice(0, VIEW_NAME_MAX_LENGTH);
      if (!name) return;
      // Capture before the async resolution — the form may be reopened before the request lands.
      const mode = this.form.mode;
      if (mode === 'rename') {
        this.renameView({ viewId: this.activeView.id, name })
          .then(() => this.toast('success', 'rename-toast'))
          .catch(() => this.toast('error', 'action-error'));
      } else {
        const create = mode === 'save-as-new' ? this.saveAsNewView : this.createView;
        create({
          name,
          scope: this.form.scope,
          allKeywords: this.form.allKeywords,
          aviseurEnabled: this.form.aviseur,
        })
          .then(() => {
            this.$emit('refresh');
            this.toast('success', mode === 'save-as-new' ? 'save-as-new-toast' : 'create-toast');
          })
          .catch(this.onCreateError);
      }
      this.closeForm();
    },
  },
  watch: {
    activeViewId() {
      this.$nextTick(this.scrollActiveIntoView);
    },
    allViews() {
      this.$nextTick(this.updateScrollState);
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.updateScrollState();
      this.scrollActiveIntoView();
    });
    window.addEventListener('resize', this.updateScrollState);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateScrollState);
  },
};
</script>

<style lang="scss">
.tender-views {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;

  // Phones: the search is inside, so nothing under it to keep off.
  @include screen-below('tablet') {
    margin-bottom: 0;
  }

  &__bar {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    border-bottom: 1px solid $color-border;

    // No views yet → no tabs to separate, so drop the divider line.
    &--empty {
      border-bottom: none;
    }
  }

  // Positioned wrapper for the scroll area: holds the edge fades + hover arrows.
  &__tabs {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;

    // Edge fades cue that more tabs exist beyond the visible area.
    &::before,
    &::after {
      content: '';
      position: absolute;
      top: 0;
      bottom: 0;
      width: 32px;
      pointer-events: none;
      opacity: 0;
      transition: opacity 120ms ease;
      z-index: 1;
    }

    &::before {
      left: 0;
      background: linear-gradient(to right, $color-white, rgba(255, 255, 255, 0));
    }

    &::after {
      right: 0;
      background: linear-gradient(to left, $color-white, rgba(255, 255, 255, 0));
    }

    &.is-ovl-left::before {
      opacity: 1;
    }

    &.is-ovl-right::after {
      opacity: 1;
    }
  }

  &__tabscroll {
    display: flex;
    align-items: center;
    gap: 2px;
    overflow-x: auto;
    scrollbar-width: none;
    min-width: 0;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  // Scroll arrows: only shown when that direction can scroll (v-show) and on hover.
  &__scroll-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    display: grid;
    place-items: center;
    width: 24px;
    height: 24px;
    border: 1px solid $color-border;
    border-radius: $radius-full;
    background: $color-white;
    box-shadow: $shadow-sm;
    color: $color-slate-500;
    cursor: pointer;
    opacity: 0;
    transition: opacity 120ms ease, color 120ms ease;

    .feather {
      width: 15px;
      height: 15px;
    }

    &:hover {
      color: $color-accent;
    }

    &--left {
      left: 0;
    }

    &--right {
      right: 0;
    }
  }

  &__tabs:hover &__scroll-btn {
    opacity: 1;
  }

  &__tab {
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    color: $color-slate-500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-family: inherit;
    font-size: 14px;
    font-weight: bold;
    padding: 9px 13px 11px;
    white-space: nowrap;
    border-radius: $border-radius-root $border-radius-root 0 0;
    transition: color ease 100ms, background ease 100ms;

    &:hover {
      color: $color-normal;
      background: $color-hover;
    }

    &.is-active {
      color: $color-accent;
      border-bottom-color: $color-accent;
    }

    &.is-dragover {
      background: rgba($color-accent-500, 0.1);
    }

    &.is-dragging {
      opacity: 0.4;
    }
  }

  // Drag-reorder grip: hidden until the tab is hovered.
  &__grip {
    color: $color-slate-300;
    cursor: grab;
    display: inline-flex;
    visibility: hidden;

    svg {
      width: 13px;
      height: 13px;
    }
  }

  &__tab:hover &__grip {
    visibility: visible;
  }

  &__preset-ico {
    width: 13px;
    height: 13px;
    color: $color-semantic-warning;
  }

  // Per-view kind icon on each tab.
  &__tab-ico {
    width: 13px;
    height: 13px;
    flex: none;
    color: $color-slate-400;

    &.is-preset {
      color: $color-semantic-warning;
    }
  }

  &__actions {
    position: relative;
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 8px;
    padding-bottom: 6px;
    flex: none;
  }

  // ---- view actions (⋯) menu ----
  &__more {
    position: relative;
  }

  &__more-btn {
    display: grid;
    place-items: center;
    width: 25px;
    height: 25px;
    border-radius: $radius-md;
    border: none;
    background: none;
    color: $color-slate-500;
    cursor: pointer;
    transition: background ease 100ms, color ease 100ms;

    .feather {
      width: 18px;
      height: 18px;
    }

    &:hover,
    &.is-open {
      background: $color-hover;
      color: $color-normal;
    }
  }

  &__moremenu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    z-index: 40;
    min-width: 240px;
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: $radius-md;
    box-shadow: $shadow-dropdown;
    padding: 8px;
  }

  &__moremenu-item {
    display: flex;
    align-items: center;
    gap: 9px;
    width: 100%;
    padding: 8px 9px;
    border: none;
    background: none;
    border-radius: $radius-sm;
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    font-weight: bold;
    color: $color-text-body;
    text-align: left;

    &:hover {
      background: $color-hover;
    }

    &.is-danger {
      color: $color-red-700;
    }

    // Only-owned-view: delete is shown but disabled, with an explanatory tooltip.
    &.is-disabled {
      color: $color-slate-400;
      cursor: not-allowed;

      &:hover {
        background: none;
      }
    }
  }

  &__moremenu-ic {
    display: grid;
    place-items: center;
    width: 16px;
    color: $color-slate-400;

    .feather {
      width: 15px;
      height: 15px;
    }
  }

  &__moremenu-item.is-danger &__moremenu-ic {
    color: $color-red-700;
  }

  &__moremenu-item.is-disabled &__moremenu-ic {
    color: $color-slate-400;
  }

  &__moremenu-label {
    flex: 1;
  }

  &__moremenu-chk {
    width: 15px;
    height: 15px;
    color: $color-accent;
    flex: none;
  }

  &__moremenu-div {
    height: 1px;
    background: $color-border;
    margin: 6px 2px;
  }

  &__allviews {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 40;
    min-width: 260px;
    max-height: 360px;
    overflow-y: auto;
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    box-shadow: $shadow-dropdown;
    padding: 6px;
  }

  &__allviews-title {
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $color-slate-400;
    margin: 6px 8px;
  }

  &__allviews-row {
    display: flex;
    align-items: center;
    gap: 9px;
    padding: 8px 9px;
    border-radius: $border-radius-small;
    cursor: pointer;
    font-size: 13px;
    font-weight: bold;
    color: $color-text-body;

    &:hover {
      background: $color-hover;
    }

    &.is-dragover {
      background: rgba($color-accent-500, 0.1);
    }

    &.is-dragging {
      opacity: 0.4;
    }

    &:hover .tender-views__allviews-grip {
      visibility: visible;
    }
  }

  &__allviews-grip {
    color: $color-slate-300;
    cursor: grab;
    display: inline-flex;
    visibility: hidden;
    flex: none;

    svg {
      width: 13px;
      height: 13px;
    }
  }

  &__allviews-ico {
    width: 15px;
    height: 15px;
    color: $color-slate-400;
    flex: none;

    &.is-preset {
      color: $color-semantic-warning;
    }
  }

  &__allviews-name {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__allviews-check {
    width: 15px;
    height: 15px;
    color: $color-accent;
    flex: none;
  }

  &__meta {
    display: flex;
    align-items: center;
    gap: 9px;
    min-height: 28px;

    // Tablet / mobile: let the dirty save actions drop under the scope badges.
    @media (max-width: 1079px) {
      flex-wrap: wrap;
    }
  }

  &__badges {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 9px;
  }

  &__save {
    margin-left: auto;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;

    @media (max-width: 1079px) {
      flex: 1 1 100%;
      margin-left: 0;
      justify-content: flex-start;
    }
  }

  // When Save-changes is disabled (uneditable view), let hover fall through the inert <button> to
  // the wrapper so BaseButton's explanatory tooltip still appears (disabled buttons eat hover).
  &__save-changes {
    &.is-locked {
      cursor: not-allowed;
    }

    .base-button__element:disabled {
      pointer-events: none;
    }
  }

  &__dirty-note {
    font-size: 12px;
    font-weight: bold;
    color: $color-semantic-warning;

    // Phones: the note takes what the two buttons leave.
    @include screen-below('tablet') {
      flex: 1;
    }
  }

  // ---- phones' views sheet ----
  &__sheet-ico.is-preset {
    color: $color-semantic-warning;
  }

  &__sheet-name {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  // The view shown: its badges say what the menu's toggles are set to.
  &__sheet-current {
    margin: 8px 0 4px;
    padding-top: 16px;
    border-top: 1px solid $color-border;
  }

  &__sheet-heading {
    margin: 0 0 8px;
    font-size: 13px;
    font-weight: bold;
    color: $color-slate-500;
  }

  &__dialog-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
  }

  // ---- dialog form (rename / create / save-as-new) ----
  &__field {
    margin-bottom: 16px;
  }

  &__field-label {
    display: block;
    font-size: 12px;
    font-weight: 800;
    color: $color-normal;
    margin-bottom: 6px;
  }

  // FormInput carries a form-layout label/padding scaffold that we don't need inside the modal.
  &__field .form-input {
    padding: 0;
  }

  &__radiorow {
    display: flex;
    gap: 10px;
  }

  &__radiocard {
    flex: 1;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    padding: 12px;
    cursor: pointer;
    background: $color-white;
    text-align: left;
    font-family: inherit;

    &.is-on {
      border-color: $color-accent;
      background: $color-accent-100;
    }
  }

  &__radiocard-title {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 13px;
    font-weight: 800;
    color: $color-normal;

    .feather {
      width: 15px;
      height: 15px;
    }
  }

  &__radiocard-desc {
    display: block;
    margin-top: 4px;
    font-size: 12px;
    color: $color-slate-500;
  }

  &__toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 10px 0;

    & + & {
      border-top: 1px solid $color-border;
    }
  }

  &__toggle-text {
    min-width: 0;
  }

  &__toggle-title {
    font-size: 13px;
    font-weight: 800;
    color: $color-normal;
  }

  &__toggle-desc {
    font-size: 12px;
    color: $color-slate-500;
    margin-top: 2px;
  }

  &__note {
    margin-top: 6px;
    padding: 11px 13px;
    background: $color-hover;
    border: 1px solid $color-border;
    border-radius: $border-radius-root;
    font-size: 12px;
    color: $color-slate-600;
  }

  &__confirm-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
    color: $color-normal;
  }
}

// Center these BaseDialog instances (the base component defaults to a right-side drawer)
// by overriding position to relative — the same approach the admin delete/add dialogs use.
.base-dialog__tender-view-dialog,
.base-dialog__tender-view-confirm {
  position: relative;
  top: 0;
  height: auto;
  width: 460px;
  max-width: 90vw;

  .base-dialog__header {
    height: auto;
    border-bottom: 1px solid $color-border;

    .header__title {
      font-size: 18px;
      margin: 18px 22px;
    }
  }

  .base-dialog__header + .base-dialog__content {
    padding: 20px 22px;
    background: $color-white;
  }

  .base-dialog__actions {
    padding: 16px 22px;
    border-top: 1px solid $color-border;
  }
}
</style>
